import type { TUserOrganization } from '@src/models/interfaces/user.interface';

import type { WorkJourneyAssignedUser, WorkJourneyForm, WorkJourneyUserSummary } from '../types';

export type RefreshAssignedUsersGetUser = (
  orgId: string,
  userId: string,
) => Promise<TUserOrganization | null>;

const resolveAssignedUserId = (assigned: WorkJourneyAssignedUser): string | undefined => {
  const nestedId = assigned?.user?.id;
  if (typeof nestedId === 'string' && nestedId) {
    return nestedId;
  }
  if (typeof assigned?.id === 'string' && assigned.id) {
    return assigned.id;
  }
  return undefined;
};

/**
 * Merges live organization-user fields into an existing journey member/supervisor
 * snapshot. Preserves the assigned-user envelope (`id`, `divisions`, …) and only
 * refreshes nested `user` data — never invents a new member row.
 */
export const mergeOrgUserIntoAssignedUser = (
  assigned: WorkJourneyAssignedUser,
  orgUser: TUserOrganization,
): WorkJourneyAssignedUser => {
  const nested: WorkJourneyUserSummary =
    assigned.user && typeof assigned.user === 'object'
      ? assigned.user
      : (assigned as WorkJourneyUserSummary);

  // Org user docs may include list fields (`disabled`, `info`) not on TUserOrganization.
  const liveUser = orgUser as TUserOrganization & WorkJourneyUserSummary;

  const refreshedUser: WorkJourneyUserSummary = {
    ...nested,
    id: liveUser.id || nested.id,
    address: liveUser.address ?? nested.address,
    idCard: liveUser.idCard || nested.idCard,
    jurisdictionId: liveUser.jurisdictionId ?? nested.jurisdictionId,
    disabled: liveUser.disabled ?? nested.disabled,
    onboardingCompleted: liveUser.onboardingCompleted ?? nested.onboardingCompleted,
    firstName: liveUser.firstName ?? nested.firstName,
    lastName: liveUser.lastName ?? nested.lastName,
    displayName: liveUser.displayName ?? nested.displayName,
    email: liveUser.email ?? nested.email,
    info: liveUser.info ?? nested.info,
  };

  return {
    ...assigned,
    user: refreshedUser,
  };
};

/**
 * Reloads each assigned user from organization users so legacy journey snapshots
 * pick up attendance address and status fields before save.
 *
 * `getUser` must be provided by the caller (typically `WorkJourneyService` via
 * `UserRepository`) — this util does not instantiate repositories.
 *
 * Failures from `getUser` (throw or missing doc) propagate so callers do not
 * validate against a stale snapshot while pretending the refresh succeeded.
 */
export const refreshWorkJourneyAssignedUsers = async (
  orgId: string,
  assignedUsers: WorkJourneyAssignedUser[] = [],
  getUser: RefreshAssignedUsersGetUser,
): Promise<WorkJourneyAssignedUser[]> => {
  if (!orgId || !Array.isArray(assignedUsers) || assignedUsers.length === 0) {
    return assignedUsers || [];
  }

  return Promise.all(
    assignedUsers.map(async assigned => {
      const userId = resolveAssignedUserId(assigned);
      if (!userId) {
        return assigned;
      }

      const orgUser = await getUser(orgId, userId);
      if (!orgUser) {
        throw new Error(
          `Organization user ${userId} was not found while refreshing work journey members`,
        );
      }
      return mergeOrgUserIntoAssignedUser(assigned, orgUser);
    }),
  );
};

export const refreshWorkJourneyMembersAndSupervisors = async (
  orgId: string,
  form: Pick<WorkJourneyForm, 'members' | 'supervisors'>,
  getUser: RefreshAssignedUsersGetUser,
): Promise<Pick<WorkJourneyForm, 'members' | 'supervisors'>> => {
  const [members, supervisors] = await Promise.all([
    refreshWorkJourneyAssignedUsers(orgId, form.members || [], getUser),
    refreshWorkJourneyAssignedUsers(orgId, form.supervisors || [], getUser),
  ]);

  return { members, supervisors };
};
